
/* ============================== src/components/Select.jsx ============================== */
// src/components/Select.jsx
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { sortOptions } from "../utils/selectOptions";

// (docstring без изменений)

const EMPTY_VALUES = Object.freeze([]);

export const Select = ({
  id,
  value,
  onChange,
  options,
  disabled,
  placeholder = "-",
  className = "",
  multiple = false,
  searchable,
  emptyMessage = "Ничего не найдено",
  searchPlaceholder = "Поиск...",
  autoSort = true,
  placement = "auto",
  footer = null,
  editable = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Пользовательский выбор хранится вместе с filterText, при котором он
  // был сделан. При смене filterText ключ перестаёт совпадать, и
  // highlightIndex пересчитывается из дефолта без setState в эффекте.
  const [userHighlight, setUserHighlight] = useState({ idx: null, key: null });

  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const searchInputRef = useRef(null);

  const values = multiple && Array.isArray(value) ? value : EMPTY_VALUES;

  const sortedOptions = useMemo(
    () => (autoSort ? sortOptions(options) : options),
    [options, autoSort],
  );

  const selectedSingle = multiple
    ? null
    : sortedOptions.find((o) => String(o.value) === String(value));

  const filterText = editable ? String(value ?? "") : search;

  const shouldSearch = editable
    ? false
    : searchable === undefined
      ? sortedOptions.length > 10
      : searchable;

  const filtered = useMemo(() => {
    if (!filterText.trim()) return sortedOptions;
    const q = filterText.toLowerCase();
    return sortedOptions.filter((o) => o.label.toLowerCase().includes(q));
  }, [sortedOptions, filterText]);

  const selectedSet = useMemo(() => new Set(values.map(String)), [values]);

  // Производный highlightIndex — заменяет прежний useEffect, который
  // вызывал setHighlightIndex и нарушал react-hooks/set-state-in-effect.
  const highlightIndex = useMemo(() => {
    if (!open) return -1;
    if (filtered.length === 0) return -1;
    if (userHighlight.key === filterText && userHighlight.idx !== null) {
      return Math.min(userHighlight.idx, filtered.length - 1);
    }
    if (search.trim() && !editable) return 0;
    if (!multiple && value !== null && value !== undefined && value !== "") {
      const idx = filtered.findIndex((o) => String(o.value) === String(value));
      return idx >= 0 ? idx : 0;
    }
    return 0;
  }, [
    open,
    filtered,
    userHighlight,
    filterText,
    search,
    editable,
    multiple,
    value,
  ]);

  const setHighlightIndex = useCallback(
    (next) => {
      setUserHighlight((prev) => ({
        idx:
          typeof next === "function"
            ? next(prev.key === filterText && prev.idx !== null ? prev.idx : 0)
            : next,
        key: filterText,
      }));
    },
    [filterText],
  );

  const openPopup = () => {
    if (disabled) return;
    setSearch("");
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setSearch("");
    setUserHighlight({ idx: null, key: null });
  };

  const toggle = () => (open ? close() : openPopup());

  useEffect(() => {
    if (open && !editable && popupRef.current && !shouldSearch)
      popupRef.current.focus();
  }, [open, shouldSearch, editable]);

  useEffect(() => {
    if (open && shouldSearch && searchInputRef.current)
      searchInputRef.current.focus();
  }, [open, shouldSearch]);

  useLayoutEffect(() => {
    if (!open || !popupRef.current || !triggerRef.current) return;

    const position = () => {
      const popup = popupRef.current;
      const trigger = triggerRef.current;
      if (!popup || !trigger) return;

      const triggerRect = trigger.getBoundingClientRect();
      const popupWidth = popup.offsetWidth;
      const popupHeight = popup.offsetHeight;

      const modalBody = trigger.closest(".modal-body");
      const containerRect = modalBody
        ? modalBody.getBoundingClientRect()
        : { top: 0, bottom: window.innerHeight };

      const vw = window.innerWidth;
      const EDGE = 8;

      const spaceBelow = Math.max(0, containerRect.bottom - triggerRect.bottom);
      const spaceAbove = Math.max(0, triggerRect.top - containerRect.top);

      let openUp;
      if (placement === "top") openUp = true;
      else if (placement === "bottom") openUp = false;
      else openUp = popupHeight > spaceBelow && spaceAbove > spaceBelow;

      let left = triggerRect.left;
      if (left + popupWidth > vw - EDGE) {
        left = Math.max(EDGE, vw - popupWidth - EDGE);
      }

      popup.style.setProperty("--select-popup-left", `${left}px`);
      popup.style.setProperty(
        "--select-popup-min-width",
        `${triggerRect.width}px`,
      );
      if (modalBody) popup.dataset.inModal = "true";
      else delete popup.dataset.inModal;

      if (openUp) {
        popup.style.setProperty("--select-popup-top", "auto");
        popup.style.setProperty(
          "--select-popup-bottom",
          `${window.innerHeight - triggerRect.top + 4}px`,
        );
      } else {
        popup.style.setProperty(
          "--select-popup-top",
          `${triggerRect.bottom + 4}px`,
        );
        popup.style.setProperty("--select-popup-bottom", "auto");
      }
    };

    position();
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    return () => {
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [open, placement]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (popupRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const handleSelect = (opt) => {
    if (multiple) {
      const next = new Set(selectedSet);
      const key = String(opt.value);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      onChange(
        sortedOptions.map((o) => o.value).filter((v) => next.has(String(v))),
      );
    } else {
      onChange(opt.value);
      close();
    }
  };

  const handlePopupKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Home") {
      e.preventDefault();
      setHighlightIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setHighlightIndex(filtered.length - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && filtered[highlightIndex]) {
        handleSelect(filtered[highlightIndex]);
      }
    }
  };

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      openPopup();
    }
  };

  const handleEditableChange = (e) => {
    const v = e.target.value;
    onChange(v);
    if (!open) setOpen(true);
    setHighlightIndex(0);
  };

  const handleEditableKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) openPopup();
      else setHighlightIndex((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) openPopup();
      else setHighlightIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      if (open && highlightIndex >= 0 && filtered[highlightIndex]) {
        e.preventDefault();
        handleSelect(filtered[highlightIndex]);
      }
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        close();
      }
    } else if (e.key === "Home" && open) {
      e.preventDefault();
      setHighlightIndex(0);
    } else if (e.key === "End" && open) {
      e.preventDefault();
      setHighlightIndex(filtered.length - 1);
    }
  };

  const renderTriggerContent = () => {
    if (multiple) {
      if (values.length === 0) {
        return (
          <span className="custom-select-single-label text-mut">
            {placeholder}
          </span>
        );
      }
      const selected = sortedOptions.filter((o) =>
        selectedSet.has(String(o.value)),
      );
      return (
        <span className="custom-select-chips">
          {selected.map((o) => (
            <span key={String(o.value)} className="sel-chip" title={o.label}>
              {o.label}
            </span>
          ))}
        </span>
      );
    }
    return (
      <span
        className={`custom-select-single-label${selectedSingle ? "" : " text-mut"}`}
      >
        {selectedSingle ? selectedSingle.label : placeholder}
      </span>
    );
  };

  const renderTrigger = () => {
    if (editable) {
      return (
        <div
          ref={triggerRef}
          className={`inp sel custom-select-trigger custom-select-trigger--editable ${className}`}
        >
          <input
            id={id}
            type="text"
            className="custom-select-input"
            value={value ?? ""}
            onChange={handleEditableChange}
            onFocus={openPopup}
            onClick={openPopup}
            onKeyDown={handleEditableKeyDown}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete="off"
            aria-haspopup="listbox"
            aria-expanded={open}
          />
          <span
            className="custom-select-arrow"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              toggle();
            }}
            aria-hidden="true"
          >
            ▾
          </span>
        </div>
      );
    }
    return (
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={`inp sel custom-select-trigger ${className}`}
        onClick={toggle}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="custom-select-trigger-content">
          {renderTriggerContent()}
        </span>
        <span className="custom-select-arrow">▾</span>
      </button>
    );
  };

  return (
    <>
      {renderTrigger()}

      {open &&
        createPortal(
          <div
            ref={popupRef}
            className="custom-select-popup"
            role="listbox"
            aria-multiselectable={multiple || undefined}
            tabIndex={-1}
            onKeyDown={handlePopupKeyDown}
          >
            {shouldSearch && (
              <div className="custom-select-search">
                <input
                  ref={searchInputRef}
                  className="inp inp-sm"
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            )}
            <div className="custom-select-list">
              {filtered.map((o, idx) => {
                const isSelected = multiple
                  ? selectedSet.has(String(o.value))
                  : String(o.value) === String(value);
                return (
                  <div
                    key={String(o.value)}
                    className={
                      "custom-select-option" +
                      (isSelected ? " on" : "") +
                      (idx === highlightIndex ? " hl" : "")
                    }
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(o)}
                    onMouseEnter={() => setHighlightIndex(idx)}
                  >
                    {multiple && (
                      <span
                        className={`custom-select-check${isSelected ? " on" : ""}`}
                      >
                        {isSelected ? "✓" : ""}
                      </span>
                    )}
                    <span className="custom-select-option-label">
                      {o.label}
                    </span>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div className="custom-select-empty">{emptyMessage}</div>
              )}
            </div>
            {footer && <div className="custom-select-footer">{footer}</div>}
          </div>,
          document.body,
        )}
    </>
  );
};

export default Select;
